import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { CaretLeft } from 'phosphor-react'
import { Header, Message } from 'semantic-ui-react'
import styled from 'styled-components'
import { EmptyBox, FlexRow, FlexRowBetween, LoaderDataBox } from 'components'
import { useNotification } from 'hooks/toast'
import {
    InspectionService,
    parseInspectionPreviewResponse,
} from 'utility/services/inspection'

const ReviewContainer = styled.div`
    .i-frame {
        height: 85vh;
        border: none;
    }
`

const resolveAuditPayload = (routeId, locationState) => {
    const fromState =
        locationState?.auditPayload || locationState?.reviewData || null
    if (fromState) {
        return fromState
    }
    if (!routeId) {
        return null
    }
    try {
        const stored = sessionStorage.getItem(
            `inspection-review-payload-${routeId}`,
        )
        return stored ? JSON.parse(stored) : null
    } catch {
        return null
    }
}

export const InspectionReviewScreen = () => {
    const { id } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const { errorToast } = useNotification()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [preview, setPreview] = useState(null)

    const previewFetchedForIdRef = useRef(null)

    const reportPath = preview?.reportPath || null

    useEffect(() => {
        if (!id) {
            setLoading(false)
            return undefined
        }

        if (previewFetchedForIdRef.current === id) {
            return undefined
        }
        previewFetchedForIdRef.current = id

        let cancelled = false

        const fetchReview = async () => {
            try {
                setLoading(true)
                setError(false)
                setErrorMessage('')

                const auditPayload = resolveAuditPayload(id, location.state)

                if (auditPayload) {
                    const response = await InspectionService.getReviewReport(
                        auditPayload,
                    )
                    if (cancelled) return

                    const parsed = parseInspectionPreviewResponse(response)

                    if (!parsed.success || !parsed.reportPath) {
                        const message =
                            parsed.errorMessage ||
                            'Preview report could not be generated.'
                        setError(true)
                        setErrorMessage(message)
                        errorToast(message)
                        return
                    }

                    setPreview(parsed)
                    return
                }

                const fallbackReport = await InspectionService.getReport(id)
                if (cancelled) return

                const parsed = parseInspectionPreviewResponse(fallbackReport)
                if (parsed.reportPath) {
                    setPreview(parsed)
                } else if (fallbackReport?.ReportPath) {
                    setPreview({
                        success: true,
                        reportPath: fallbackReport.ReportPath,
                        errorMessage: null,
                        auditId: fallbackReport.AuditId || id,
                    })
                } else {
                    setError(true)
                    setErrorMessage('No preview data available.')
                }
            } catch (err) {
                if (cancelled) return
                console.error('Review API error:', err)
                const message =
                    err?.error?.Message ||
                    err?.error?.message ||
                    err?.message ||
                    'Failed to load preview report.'
                setError(true)
                setErrorMessage(message)
                errorToast(message)
            } finally {
                if (!cancelled) {
                    setLoading(false)
                }
            }
        }

        fetchReview()

        return () => {
            cancelled = true
            if (previewFetchedForIdRef.current === id) {
                previewFetchedForIdRef.current = null
            }
        }
    }, [id])

    const emptyMessage = useMemo(() => {
        if (errorMessage) return errorMessage
        return 'No preview report available.'
    }, [errorMessage])

    return (
        <ReviewContainer className="mt-5">
            <Header as="h4" block>
                <FlexRowBetween>
                    <FlexRow
                        onClick={() => navigate(-1)}
                        className="align-items-center pointer"
                    >
                        <CaretLeft size="24" />
                        <div className="ms-2">Back to Inspection</div>
                    </FlexRow>
                </FlexRowBetween>
            </Header>
            {loading && <LoaderDataBox />}
            {!loading && error && (
                <Message negative>
                    <Message.Header>Preview failed</Message.Header>
                    <p>{emptyMessage}</p>
                </Message>
            )}
            {!loading && !error && !reportPath && <EmptyBox />}
            {!loading && reportPath && (
                <div className="mt-3">
                    <iframe
                        src={reportPath}
                        width="100%"
                        className="i-frame"
                        title="Review Report"
                    />
                </div>
            )}
        </ReviewContainer>
    )
}
